import type { SupabaseClient } from "@supabase/supabase-js";
import type { IdentityCapabilityStatus, PartyChannelType } from "../../lib/types";
import type {
  CreateOrReuseCompanyLeadInput,
  CreateRelatedLeadInput,
  IdentityCandidateReviewDecision,
  MergeCompaniesMutationResult,
  MergeCompaniesInput,
  MergeCompaniesPreview,
  MergeCompanyField,
  ReclassifyPartyChannelMutationResult,
  ReclassifyPartyChannelPreview,
  ReclassifyPartyChannelInput,
  ReviewIdentityCandidateInput,
  ReviewIdentityCandidateMutationResult,
  RelatedLeadAction,
  RelatedLeadMutationResult,
  RelatedLeadPreview,
} from "./commands";
import type {
  CompanyRow,
  ContactRow,
  IdentityCandidateRow,
  LeadContactRow,
  LeadRelationRow,
  PartyChannelRow,
} from "./mappers";
import { normalizeCnpj, type IdentityInput } from "./identity";

export type IdentityCapabilities = {
  status: IdentityCapabilityStatus;
  schemaVersion: number;
  identityFoundation: boolean;
  canonicalLeadCreation: boolean;
  canonicalLeadCreationCanaryReady: boolean;
  canonicalLeadCreationCanary: boolean;
  taskCommands: boolean;
  emailInbox: boolean;
  emailSend: boolean;
  aiAssistance: boolean;
  aiAutoSend: boolean;
  flowDefinitions: boolean;
  flowPublication: boolean;
  flowExecution: boolean;
  finance: boolean;
  message?: string;
};

export type IdentityRpcResult = Record<string, unknown>;

function isRpcRecord(value: unknown): value is IdentityRpcResult {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function rpcRecord(value: unknown, invalidMessage: string): IdentityRpcResult {
  if (!isRpcRecord(value)) throw new Error(invalidMessage);
  return value;
}

function rpcField(payload: IdentityRpcResult, key: string): unknown {
  try {
    return Object.prototype.hasOwnProperty.call(payload, key) ? payload[key] : undefined;
  } catch {
    throw new Error("Resposta RPC inválida.");
  }
}

function isDenseRpcArray(value: unknown): value is unknown[] {
  try {
    if (!Array.isArray(value)) return false;
    for (let index = 0; index < value.length; index += 1) {
      if (!Object.prototype.hasOwnProperty.call(value, index)) return false;
    }
    return true;
  } catch {
    return false;
  }
}

export type CreateOrReuseCompanyLeadRemoteResult = {
  outcome: "created" | "reused" | "reactivated";
  idempotent: boolean;
  commandId: string;
  companyId: string;
  leadId: string;
  membershipId: string;
  contactIds: string[];
  primaryContactId?: string;
  visitId?: string;
  eventIds: string[];
  warnings: string[];
};

export type RelatedLeadPreviewRemoteResult = RelatedLeadPreview;
export type CreateRelatedLeadRemoteResult = RelatedLeadMutationResult;
export type ReviewIdentityCandidateRemoteResult = ReviewIdentityCandidateMutationResult;
export type ReclassifyPartyChannelPreviewRemoteResult = ReclassifyPartyChannelPreview;
export type ReclassifyPartyChannelRemoteResult = ReclassifyPartyChannelMutationResult;
export type MergeCompaniesPreviewRemoteResult = MergeCompaniesPreview;
export type MergeCompaniesRemoteResult = MergeCompaniesMutationResult;

function requiredRpcString(payload: IdentityRpcResult, key: string, invalidMessage = "Resposta inválida da criação canônica candidata") {
  const value = rpcField(payload, key);
  const normalized = typeof value === "string" ? value.trim() : "";
  if (!normalized) throw new Error(`${invalidMessage}: ${key}.`);
  return normalized;
}

function rpcStringArray(payload: IdentityRpcResult, key: string, invalidMessage = "Resposta inválida da criação canônica candidata") {
  const value = rpcField(payload, key);
  if (!isDenseRpcArray(value) || value.some((item) => typeof item !== "string" || !item.trim()))
    throw new Error(`${invalidMessage}: ${key}.`);
  return value.map((item) => (item as string).trim());
}

function optionalRpcString(payload: IdentityRpcResult, key: string, fallback?: string, invalidMessage = "Resposta inválida da continuidade") {
  const value = rpcField(payload, key);
  if (value === null || value === undefined) return fallback;
  const normalized = typeof value === "string" ? value.trim() : "";
  if (!normalized) throw new Error(`${invalidMessage}: ${key}.`);
  return normalized;
}

function rpcBoolean(payload: IdentityRpcResult, key: string, fallback: boolean, invalidMessage = "Resposta inválida da continuidade") {
  const value = rpcField(payload, key);
  if (value === undefined || value === null) return fallback;
  if (typeof value !== "boolean") throw new Error(`${invalidMessage}: ${key}.`);
  return value;
}

const relatedLeadActions = new Set<RelatedLeadAction>(["criar", "reutilizar", "reativar", "bloquear"]);

export function parseRelatedLeadPreview(
  value: unknown,
  defaults: Pick<RelatedLeadPreview, "sourceLeadId" | "targetFunnelId">,
): RelatedLeadPreviewRemoteResult {
  const invalidMessage = "Resposta inválida da prévia de continuidade.";
  const payload = rpcRecord(value, invalidMessage);
  const action = rpcField(payload, "action");
  if (typeof action !== "string" || !relatedLeadActions.has(action as RelatedLeadAction))
    throw new Error(`${invalidMessage}: action.`);
  const sourceLeadId = optionalRpcString(payload, "source_lead_id", defaults.sourceLeadId, invalidMessage);
  const targetFunnelId = optionalRpcString(payload, "target_funnel_id", defaults.targetFunnelId, invalidMessage);
  if (!sourceLeadId || !targetFunnelId)
    throw new Error(`${invalidMessage}: identidade da origem.`);
  const targetLeadId = optionalRpcString(payload, "target_lead_id", undefined, invalidMessage);
  const sharedValue = rpcField(payload, "shared");
  const shared = sharedValue === undefined || sharedValue === null
    ? []
    : rpcStringArray(payload, "shared", invalidMessage);
  const isolatedValue = rpcField(payload, "isolated");
  const isolated = isolatedValue === undefined || isolatedValue === null
    ? []
    : rpcStringArray(payload, "isolated", invalidMessage);
  return {
    action: action as RelatedLeadAction,
    sourceLeadId,
    targetFunnelId,
    companyId: optionalRpcString(payload, "company_id", undefined, invalidMessage),
    sourceMembershipId: optionalRpcString(payload, "source_membership_id", undefined, invalidMessage),
    targetLeadId,
    targetMembershipId: optionalRpcString(payload, "target_membership_id", undefined, invalidMessage),
    reason: optionalRpcString(payload, "reason", undefined, invalidMessage),
    sharedContext: shared,
    isolatedContext: isolated,
    requiresConfirmation: rpcBoolean(payload, "requires_confirmation", false, invalidMessage),
  };
}

export function parseCreateRelatedLeadResult(value: unknown): CreateRelatedLeadRemoteResult {
  const invalidMessage = "Resposta inválida da continuidade.";
  const payload = rpcRecord(value, invalidMessage);
  const action = rpcField(payload, "action");
  const idempotent = rpcField(payload, "idempotent");
  if (action !== "criar" && action !== "reutilizar" && action !== "reativar")
    throw new Error(`${invalidMessage}: action.`);
  if (typeof idempotent !== "boolean")
    throw new Error(`${invalidMessage}: idempotent.`);
  return {
    action,
    idempotent,
    sourceLeadId: requiredRpcString(payload, "source_lead_id", invalidMessage),
    targetLeadId: requiredRpcString(payload, "target_lead_id", invalidMessage),
    relationId: requiredRpcString(payload, "relation_id", invalidMessage),
    commandId: requiredRpcString(payload, "command_id", invalidMessage),
    warnings: rpcStringArray(payload, "warnings", invalidMessage),
  };
}

export function parseReviewIdentityCandidateResult(value: unknown): ReviewIdentityCandidateRemoteResult {
  const invalidMessage = "Resposta inválida da revisão do candidato.";
  const payload = rpcRecord(value, invalidMessage);
  const decision = rpcField(payload, "status");
  const idempotent = rpcField(payload, "idempotent");
  if (decision !== "accepted" && decision !== "rejected")
    throw new Error(`${invalidMessage}: status.`);
  if (typeof idempotent !== "boolean")
    throw new Error(`${invalidMessage}: idempotent.`);
  return {
    candidateId: requiredRpcString(payload, "candidate_id", invalidMessage),
    decision: decision as IdentityCandidateReviewDecision,
    idempotent,
    commandId: requiredRpcString(payload, "command_id", invalidMessage),
  };
}

const partyChannelTypes = new Set<PartyChannelType>(["email", "phone", "instagram"]);

const mergeCompanyFieldNames: Record<string, MergeCompanyField> = {
  legal_name: "legalName",
  trade_name: "tradeName",
  cnpj: "cnpj",
  site: "site",
  segment: "segment",
  city: "city",
  state: "state",
};

function parseMergeCompanyCounts(value: unknown, key: string, invalidMessage: string) {
  if (!isRpcRecord(value))
    throw new Error(`${invalidMessage}: ${key}.`);
  const counts = value as Record<string, unknown>;
  const parsed = {
    contacts: rpcField(counts, "contacts"),
    channels: rpcField(counts, "channels"),
    leads: rpcField(counts, "leads"),
  };
  if (Object.values(parsed).some((count) => typeof count !== "number" || !Number.isSafeInteger(count) || count < 0))
    throw new Error(`${invalidMessage}: ${key}.`);
  return parsed as { contacts: number; channels: number; leads: number };
}

function parseMergeCompanyFields(value: unknown, key: string, invalidMessage: string): MergeCompanyField[] {
  if (!isDenseRpcArray(value))
    throw new Error(`${invalidMessage}: ${key}.`);
  const fields: MergeCompanyField[] = [];
  for (const field of value) {
    if (typeof field !== "string" || !Object.prototype.hasOwnProperty.call(mergeCompanyFieldNames, field))
      throw new Error(`${invalidMessage}: ${key}.`);
    fields.push(mergeCompanyFieldNames[field]);
  }
  if (new Set(fields).size !== fields.length)
    throw new Error(`${invalidMessage}: ${key}.`);
  return fields;
}

export function parseMergeCompaniesPreviewResult(value: unknown): MergeCompaniesPreviewRemoteResult {
  const invalidMessage = "Resposta inválida da prévia do merge";
  const payload = rpcRecord(value, invalidMessage);
  if (rpcField(payload, "source_status") !== "active" || rpcField(payload, "target_status") !== "active")
    throw new Error(`${invalidMessage}: status.`);
  return {
    sourceCompanyId: requiredRpcString(payload, "source_company_id", invalidMessage),
    targetCompanyId: requiredRpcString(payload, "target_company_id", invalidMessage),
    conflicts: parseMergeCompanyFields(rpcField(payload, "conflicts"), "conflicts", invalidMessage),
    sourceCounts: parseMergeCompanyCounts(rpcField(payload, "source_counts"), "source_counts", invalidMessage),
    targetCounts: parseMergeCompanyCounts(rpcField(payload, "target_counts"), "target_counts", invalidMessage),
    requiresConfirmation: rpcBoolean(payload, "requires_confirmation", false, invalidMessage),
  };
}

export function parseMergeCompaniesResult(value: unknown): MergeCompaniesRemoteResult {
  const invalidMessage = "Resposta inválida do merge";
  const payload = rpcRecord(value, invalidMessage);
  const idempotent = rpcField(payload, "idempotent");
  if (rpcField(payload, "outcome") !== "merged")
    throw new Error(`${invalidMessage}: outcome.`);
  if (typeof idempotent !== "boolean")
    throw new Error(`${invalidMessage}: idempotent.`);
  if (rpcField(payload, "history_rewritten") !== false)
    throw new Error(`${invalidMessage}: history_rewritten.`);
  const sourceCompanyId = requiredRpcString(payload, "source_company_id", invalidMessage);
  const targetCompanyId = requiredRpcString(payload, "target_company_id", invalidMessage);
  const tombstone = rpcField(payload, "tombstone");
  if (!isRpcRecord(tombstone))
    throw new Error(`${invalidMessage}: tombstone.`);
  const parsedTombstone = tombstone as Record<string, unknown>;
  if (rpcField(parsedTombstone, "status") !== "merged")
    throw new Error(`${invalidMessage}: tombstone.status.`);
  const tombstoneCompanyId = requiredRpcString(parsedTombstone, "company_id", invalidMessage);
  const mergedIntoId = requiredRpcString(parsedTombstone, "merged_into_id", invalidMessage);
  if (tombstoneCompanyId !== sourceCompanyId || mergedIntoId !== targetCompanyId)
    throw new Error(`${invalidMessage}: tombstone.relation.`);
  return {
    idempotent,
    sourceCompanyId,
    targetCompanyId,
    commandId: requiredRpcString(payload, "command_id", invalidMessage),
    tombstone: {
      companyId: tombstoneCompanyId,
      mergedIntoId,
      status: "merged",
    },
    preservedEntityIds: rpcStringArray(payload, "preserved_entity_ids", invalidMessage),
    historyRewritten: false,
  };
}

export function parseReclassifyPartyChannelPreviewResult(value: unknown): ReclassifyPartyChannelPreviewRemoteResult {
  const invalidMessage = "Resposta inválida da prévia do canal";
  const payload = rpcRecord(value, invalidMessage);
  const action = rpcField(payload, "action");
  if (action !== "reclassify" && action !== "block")
    throw new Error(`${invalidMessage}: action.`);
  const channelType = rpcField(payload, "channel_type");
  if (typeof channelType !== "string" || !partyChannelTypes.has(channelType as PartyChannelType))
    throw new Error(`${invalidMessage}: channel_type.`);
  return {
    channelId: requiredRpcString(payload, "channel_id", invalidMessage),
    companyId: requiredRpcString(payload, "company_id", invalidMessage),
    fromContactId: optionalRpcString(payload, "from_contact_id", undefined, invalidMessage),
    toContactId: optionalRpcString(payload, "to_contact_id", undefined, invalidMessage),
    channelType: channelType as PartyChannelType,
    normalizedValue: requiredRpcString(payload, "normalized_value", invalidMessage),
    action,
    conflicts: rpcStringArray(payload, "conflicts", invalidMessage),
    impact: rpcStringArray(payload, "impact", invalidMessage),
    requiresConfirmation: rpcBoolean(payload, "requires_confirmation", false, invalidMessage),
  };
}

export function parseReclassifyPartyChannelResult(value: unknown): ReclassifyPartyChannelRemoteResult {
  const invalidMessage = "Resposta inválida da reclassificação do canal";
  const payload = rpcRecord(value, invalidMessage);
  const idempotent = rpcField(payload, "idempotent");
  if (typeof idempotent !== "boolean")
    throw new Error(`${invalidMessage}: idempotent.`);
  return {
    channelId: requiredRpcString(payload, "channel_id", invalidMessage),
    targetContactId: optionalRpcString(payload, "contact_id", undefined, invalidMessage),
    commandId: requiredRpcString(payload, "command_id", invalidMessage),
    idempotent,
  };
}

export function parseCreateOrReuseCompanyLeadResult(value: unknown): CreateOrReuseCompanyLeadRemoteResult {
  const invalidMessage = "Resposta inválida da criação canônica candidata";
  const payload = rpcRecord(value, invalidMessage);
  const outcome = rpcField(payload, "outcome");
  const idempotent = rpcField(payload, "idempotent");
  if (outcome !== "created" && outcome !== "reused" && outcome !== "reactivated")
    throw new Error(`${invalidMessage}: outcome.`);
  if (typeof idempotent !== "boolean")
    throw new Error(`${invalidMessage}: idempotent.`);
  const primaryContactId = rpcField(payload, "primary_contact_id");
  const visitId = rpcField(payload, "visit_id");
  if (primaryContactId !== null && primaryContactId !== undefined && (typeof primaryContactId !== "string" || !primaryContactId.trim()))
    throw new Error(`${invalidMessage}: primary_contact_id.`);
  if (visitId !== null && visitId !== undefined && (typeof visitId !== "string" || !visitId.trim()))
    throw new Error(`${invalidMessage}: visit_id.`);
  return {
    outcome,
    idempotent,
    commandId: requiredRpcString(payload, "command_id", invalidMessage),
    companyId: requiredRpcString(payload, "company_id", invalidMessage),
    leadId: requiredRpcString(payload, "lead_id", invalidMessage),
    membershipId: requiredRpcString(payload, "membership_id", invalidMessage),
    contactIds: rpcStringArray(payload, "contact_ids", invalidMessage),
    primaryContactId: typeof primaryContactId === "string" ? primaryContactId.trim() || undefined : undefined,
    visitId: typeof visitId === "string" ? visitId.trim() || undefined : undefined,
    eventIds: rpcStringArray(payload, "event_ids", invalidMessage),
    warnings: rpcStringArray(payload, "warnings", invalidMessage),
  };
}

async function invokeIdentityRpc(
  client: SupabaseClient,
  name: string,
  args: Record<string, unknown>,
  invalidResponseMessage: string,
): Promise<IdentityRpcResult> {
  const result = await client.rpc(name, args);
  if (result.error) throw result.error;
  return rpcRecord(result.data, invalidResponseMessage);
}

function identityInputRecord(value: unknown, invalidMessage: string): IdentityRpcResult {
  if (!isRpcRecord(value)) throw new Error(invalidMessage);
  return value;
}

function identityInputKeys(payload: IdentityRpcResult, invalidMessage: string): string[] {
  try {
    return Object.keys(payload);
  } catch {
    throw new Error(invalidMessage);
  }
}

function identityInputHasField(payload: IdentityRpcResult, key: string, invalidMessage: string): boolean {
  try {
    return Object.prototype.hasOwnProperty.call(payload, key);
  } catch {
    throw new Error(invalidMessage);
  }
}

function identityInputField(payload: IdentityRpcResult, key: string, invalidMessage: string): unknown {
  try {
    return identityInputHasField(payload, key, invalidMessage) ? payload[key] : undefined;
  } catch {
    throw new Error(invalidMessage);
  }
}

function assertIdentityAllowedInputKeys(
  payload: IdentityRpcResult,
  allowed: readonly string[],
  invalidMessage: string,
): void {
  for (const key of identityInputKeys(payload, invalidMessage)) {
    if (!allowed.includes(key)) throw new Error(`${invalidMessage}: ${key}.`);
  }
}

function hasIdentityControlCharacter(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code < 0x20 || code === 0x7f) return true;
  }
  return false;
}

function identityInputIdentifier(value: unknown, key: string, invalidMessage: string): string {
  const normalized = typeof value === "string" ? value.trim() : value;
  if (
    typeof normalized !== "string" ||
    !normalized ||
    normalized.length > 255 ||
    normalized === "." ||
    normalized === ".." ||
    normalized.includes("/") ||
    normalized.includes("\\") ||
    hasIdentityControlCharacter(normalized)
  ) {
    throw new Error(`${invalidMessage}: ${key}.`);
  }
  return normalized;
}

function identityInputIdentifierArray(value: unknown, key: string, invalidMessage: string): string[] {
  if (!isDenseRpcArray(value)) throw new Error(`${invalidMessage}: ${key}.`);
  const normalized = value.map((item) => identityInputIdentifier(item, key, invalidMessage));
  if (new Set(normalized).size !== normalized.length) throw new Error(`${invalidMessage}: ${key}.`);
  return normalized;
}

function identityInputIdempotencyKey(value: unknown, key: string, invalidMessage: string): string {
  const normalized = identityInputText(value, key, invalidMessage);
  if (normalized.length > 255 || hasIdentityControlCharacter(normalized))
    throw new Error(`${invalidMessage}: ${key}.`);
  return normalized;
}

function identityInputBoolean(value: unknown, key: string, invalidMessage: string): boolean {
  if (typeof value !== "boolean") throw new Error(`${invalidMessage}: ${key}.`);
  return value;
}

function identityOptionalInputBoolean(
  payload: IdentityRpcResult,
  key: string,
  invalidMessage: string,
): boolean | undefined {
  const value = identityInputField(payload, key, invalidMessage);
  if (value === undefined) return undefined;
  return identityInputBoolean(value, key, invalidMessage);
}

function identityOptionalInputTextValue(
  payload: IdentityRpcResult,
  key: string,
  invalidMessage: string,
): string | undefined {
  return identityOptionalInputText(payload, key, invalidMessage) ?? undefined;
}

function identityOptionalInputIdentifier(
  payload: IdentityRpcResult,
  key: string,
  invalidMessage: string,
): string | undefined {
  const value = identityInputField(payload, key, invalidMessage);
  if (value === undefined || value === null) return undefined;
  return identityInputIdentifier(value, key, invalidMessage);
}

function identityInputText(value: unknown, key: string, invalidMessage: string): string {
  if (typeof value !== "string" || !value.trim()) throw new Error(`${invalidMessage}: ${key}.`);
  return value.trim();
}

function identityInputName(value: unknown, key: string, invalidMessage: string): string {
  return identityInputText(value, key, invalidMessage).replace(/\s+/g, " ");
}

function identityOptionalInputText(
  payload: IdentityRpcResult,
  key: string,
  invalidMessage: string,
): string | null {
  const value = identityInputField(payload, key, invalidMessage);
  if (value === null || value === undefined) return null;
  if (typeof value !== "string") throw new Error(`${invalidMessage}: ${key}.`);
  return value.trim() || null;
}

function identityInputTimestamp(value: unknown, key: string, invalidMessage: string): string {
  const normalized = identityInputText(value, key, invalidMessage);
  if (!Number.isFinite(Date.parse(normalized))) throw new Error(`${invalidMessage}: ${key}.`);
  return normalized;
}

type IdentityCnpjInput = {
  cnpj: string | null;
  normalizedCnpj: string | null;
};

function parseIdentityCnpj(payload: IdentityRpcResult, invalidMessage: string): IdentityCnpjInput {
  const cnpj = identityOptionalInputText(payload, "cnpj", invalidMessage);
  const normalizedCnpj = cnpj === null ? null : normalizeCnpj(cnpj);
  if (cnpj !== null && !normalizedCnpj)
    throw new Error(`${invalidMessage}: cnpj.`);
  const suppliedNormalized = identityOptionalInputText(payload, "normalizedCnpj", invalidMessage);
  if (
    suppliedNormalized !== null &&
    (normalizedCnpj === null || suppliedNormalized !== normalizedCnpj)
  ) {
    throw new Error(`${invalidMessage}: normalizedCnpj.`);
  }
  return { cnpj, normalizedCnpj };
}

function assertIdentityTombstoneField(
  payload: IdentityRpcResult,
  key: string,
  invalidMessage: string,
): void {
  const value = identityInputField(payload, key, invalidMessage);
  if (value !== undefined && value !== null)
    throw new Error(`${invalidMessage}: ${key}.`);
}

type IdentityCompanyWrite = {
  row: Record<string, unknown>;
};

type IdentityCompanyUpdateWrite = IdentityCompanyWrite & {
  id: string;
};

function parseIdentityCompanyCreateInput(ownerId: unknown, value: unknown): IdentityCompanyWrite {
  const invalidMessage = "A empresa para persistência é inválida.";
  const payload = identityInputRecord(value, invalidMessage);
  assertIdentityAllowedInputKeys(payload, [
    "id", "legalName", "tradeName", "cnpj", "normalizedCnpj", "site", "segment", "city", "state",
    "status", "mergedIntoId", "createdAt", "updatedAt",
  ], invalidMessage);
  const safeOwnerId = identityInputIdentifier(ownerId, "ownerId", invalidMessage);
  const id = identityInputIdentifier(identityInputField(payload, "id", invalidMessage), "id", invalidMessage);
  const legalName = identityInputName(identityInputField(payload, "legalName", invalidMessage), "legalName", invalidMessage);
  const cnpj = parseIdentityCnpj(payload, invalidMessage);
  if (identityInputField(payload, "status", invalidMessage) !== "active")
    throw new Error(`${invalidMessage}: status.`);
  assertIdentityTombstoneField(payload, "mergedIntoId", invalidMessage);
  identityInputTimestamp(identityInputField(payload, "createdAt", invalidMessage), "createdAt", invalidMessage);
  identityInputTimestamp(identityInputField(payload, "updatedAt", invalidMessage), "updatedAt", invalidMessage);
  return {
    row: {
      id,
      owner_id: safeOwnerId,
      legal_name: legalName,
      trade_name: identityOptionalInputText(payload, "tradeName", invalidMessage),
      cnpj: cnpj.cnpj,
      site: identityOptionalInputText(payload, "site", invalidMessage),
      segment: identityOptionalInputText(payload, "segment", invalidMessage),
      city: identityOptionalInputText(payload, "city", invalidMessage),
      state: identityOptionalInputText(payload, "state", invalidMessage),
    },
  };
}

function parseIdentityCompanyUpdateInput(id: unknown, value: unknown): IdentityCompanyUpdateWrite {
  const invalidMessage = "A atualização da empresa é inválida.";
  const payload = identityInputRecord(value, invalidMessage);
  assertIdentityAllowedInputKeys(payload, [
    "legalName", "tradeName", "cnpj", "normalizedCnpj", "site", "segment", "city", "state", "updatedAt",
  ], invalidMessage);
  const safeId = identityInputIdentifier(id, "id", invalidMessage);
  const cnpj = parseIdentityCnpj(payload, invalidMessage);
  if (identityInputHasField(payload, "updatedAt", invalidMessage))
    identityInputTimestamp(identityInputField(payload, "updatedAt", invalidMessage), "updatedAt", invalidMessage);
  const row: Record<string, unknown> = {};
  if (identityInputHasField(payload, "legalName", invalidMessage))
    row.legal_name = identityInputName(identityInputField(payload, "legalName", invalidMessage), "legalName", invalidMessage);
  if (identityInputHasField(payload, "tradeName", invalidMessage))
    row.trade_name = identityOptionalInputText(payload, "tradeName", invalidMessage);
  if (identityInputHasField(payload, "cnpj", invalidMessage)) row.cnpj = cnpj.cnpj;
  if (identityInputHasField(payload, "site", invalidMessage))
    row.site = identityOptionalInputText(payload, "site", invalidMessage);
  if (identityInputHasField(payload, "segment", invalidMessage))
    row.segment = identityOptionalInputText(payload, "segment", invalidMessage);
  if (identityInputHasField(payload, "city", invalidMessage))
    row.city = identityOptionalInputText(payload, "city", invalidMessage);
  if (identityInputHasField(payload, "state", invalidMessage))
    row.state = identityOptionalInputText(payload, "state", invalidMessage);
  if (!Object.keys(row).length) throw new Error(invalidMessage);
  return { id: safeId, row };
}

type IdentityContactWrite = {
  row: Record<string, unknown>;
};

type IdentityContactUpdateWrite = IdentityContactWrite & {
  id: string;
};

function parseIdentityContactCreateInput(ownerId: unknown, value: unknown): IdentityContactWrite {
  const invalidMessage = "O contato para persistência é inválido.";
  const payload = identityInputRecord(value, invalidMessage);
  assertIdentityAllowedInputKeys(payload, [
    "id", "companyId", "name", "role", "photoUrl", "mergedIntoId", "createdAt", "updatedAt",
  ], invalidMessage);
  const safeOwnerId = identityInputIdentifier(ownerId, "ownerId", invalidMessage);
  const id = identityInputIdentifier(identityInputField(payload, "id", invalidMessage), "id", invalidMessage);
  const companyId = identityInputIdentifier(identityInputField(payload, "companyId", invalidMessage), "companyId", invalidMessage);
  const name = identityInputName(identityInputField(payload, "name", invalidMessage), "name", invalidMessage);
  assertIdentityTombstoneField(payload, "mergedIntoId", invalidMessage);
  identityInputTimestamp(identityInputField(payload, "createdAt", invalidMessage), "createdAt", invalidMessage);
  identityInputTimestamp(identityInputField(payload, "updatedAt", invalidMessage), "updatedAt", invalidMessage);
  return {
    row: {
      id,
      owner_id: safeOwnerId,
      company_id: companyId,
      name,
      role: identityOptionalInputText(payload, "role", invalidMessage),
      photo_url: identityOptionalInputText(payload, "photoUrl", invalidMessage),
    },
  };
}

function parseIdentityContactUpdateInput(id: unknown, value: unknown): IdentityContactUpdateWrite {
  const invalidMessage = "A atualização do contato é inválida.";
  const payload = identityInputRecord(value, invalidMessage);
  assertIdentityAllowedInputKeys(payload, ["name", "role", "photoUrl", "companyId", "updatedAt"], invalidMessage);
  const safeId = identityInputIdentifier(id, "id", invalidMessage);
  if (
    identityInputHasField(payload, "companyId", invalidMessage) &&
    identityInputField(payload, "companyId", invalidMessage) !== undefined
  ) {
    throw new Error(`${invalidMessage}: companyId.`);
  }
  if (identityInputHasField(payload, "updatedAt", invalidMessage))
    identityInputTimestamp(identityInputField(payload, "updatedAt", invalidMessage), "updatedAt", invalidMessage);
  const row: Record<string, unknown> = {};
  if (identityInputHasField(payload, "name", invalidMessage))
    row.name = identityInputName(identityInputField(payload, "name", invalidMessage), "name", invalidMessage);
  if (identityInputHasField(payload, "role", invalidMessage))
    row.role = identityOptionalInputText(payload, "role", invalidMessage);
  if (identityInputHasField(payload, "photoUrl", invalidMessage))
    row.photo_url = identityOptionalInputText(payload, "photoUrl", invalidMessage);
  if (!Object.keys(row).length) throw new Error(invalidMessage);
  return { id: safeId, row };
}

type IdentityLeadContactsWrite = {
  leadId: string;
  contactIds: string[];
  primaryContactId: string | null;
};

function parseIdentityLeadContactsInput(
  leadId: unknown,
  contactIds: unknown,
  primaryContactId: unknown,
): IdentityLeadContactsWrite {
  const invalidMessage = "Os vínculos de contatos do Lead para persistência são inválidos.";
  const safeLeadId = identityInputIdentifier(leadId, "leadId", invalidMessage);
  const safeContactIds = identityInputIdentifierArray(contactIds, "contactIds", invalidMessage);
  const safePrimaryContactId = primaryContactId === null || primaryContactId === undefined
    ? null
    : identityInputIdentifier(primaryContactId, "primaryContactId", invalidMessage);
  if (safePrimaryContactId !== null && !safeContactIds.includes(safePrimaryContactId))
    throw new Error(`${invalidMessage}: primaryContactId.`);
  return { leadId: safeLeadId, contactIds: safeContactIds, primaryContactId: safePrimaryContactId };
}

function parseIdentityRelatedLeadReferences(
  sourceLeadId: unknown,
  targetFunnelId: unknown,
): { sourceLeadId: string; targetFunnelId: string } {
  const invalidMessage = "Os IDs da continuidade entre funis são inválidos.";
  return {
    sourceLeadId: identityInputIdentifier(sourceLeadId, "sourceLeadId", invalidMessage),
    targetFunnelId: identityInputIdentifier(targetFunnelId, "targetFunnelId", invalidMessage),
  };
}

function parseIdentityCreateRelatedLeadInput(value: unknown): CreateRelatedLeadInput {
  const invalidMessage = "A continuidade entre funis para persistência é inválida.";
  const payload = identityInputRecord(value, invalidMessage);
  assertIdentityAllowedInputKeys(payload, [
    "sourceLeadId", "targetFunnelId", "targetStageId", "contactIds", "idempotencyKey", "reason", "confirm",
  ], invalidMessage);
  const contactIdsValue = identityInputField(payload, "contactIds", invalidMessage);
  return {
    sourceLeadId: identityInputIdentifier(identityInputField(payload, "sourceLeadId", invalidMessage), "sourceLeadId", invalidMessage),
    targetFunnelId: identityInputIdentifier(identityInputField(payload, "targetFunnelId", invalidMessage), "targetFunnelId", invalidMessage),
    targetStageId: identityInputIdentifier(identityInputField(payload, "targetStageId", invalidMessage), "targetStageId", invalidMessage),
    contactIds: contactIdsValue === undefined ? [] : identityInputIdentifierArray(contactIdsValue, "contactIds", invalidMessage),
    idempotencyKey: identityInputIdempotencyKey(identityInputField(payload, "idempotencyKey", invalidMessage), "idempotencyKey", invalidMessage),
    reason: identityInputText(identityInputField(payload, "reason", invalidMessage), "reason", invalidMessage),
    confirm: identityInputBoolean(identityInputField(payload, "confirm", invalidMessage), "confirm", invalidMessage),
  };
}

function parseIdentityReviewCandidateInput(value: unknown): ReviewIdentityCandidateInput {
  const invalidMessage = "A revisão do candidato para persistência é inválida.";
  const payload = identityInputRecord(value, invalidMessage);
  assertIdentityAllowedInputKeys(payload, ["candidateId", "decision", "reason", "idempotencyKey", "confirm"], invalidMessage);
  const decision = identityInputField(payload, "decision", invalidMessage);
  if (decision !== "accepted" && decision !== "rejected")
    throw new Error(`${invalidMessage}: decision.`);
  return {
    candidateId: identityInputIdentifier(identityInputField(payload, "candidateId", invalidMessage), "candidateId", invalidMessage),
    decision,
    reason: identityInputText(identityInputField(payload, "reason", invalidMessage), "reason", invalidMessage),
    idempotencyKey: identityInputIdempotencyKey(identityInputField(payload, "idempotencyKey", invalidMessage), "idempotencyKey", invalidMessage),
    confirm: identityInputBoolean(identityInputField(payload, "confirm", invalidMessage), "confirm", invalidMessage),
  };
}

function parseIdentityReclassifyChannelInput(value: unknown): ReclassifyPartyChannelInput {
  const invalidMessage = "A reclassificação do canal para persistência é inválida.";
  const payload = identityInputRecord(value, invalidMessage);
  assertIdentityAllowedInputKeys(payload, ["channelId", "targetContactId", "reason", "idempotencyKey", "confirm"], invalidMessage);
  const targetContactId = identityInputField(payload, "targetContactId", invalidMessage);
  return {
    channelId: identityInputIdentifier(identityInputField(payload, "channelId", invalidMessage), "channelId", invalidMessage),
    targetContactId: targetContactId === null || targetContactId === undefined
      ? targetContactId
      : identityInputIdentifier(targetContactId, "targetContactId", invalidMessage),
    reason: identityInputText(identityInputField(payload, "reason", invalidMessage), "reason", invalidMessage),
    idempotencyKey: identityInputIdempotencyKey(identityInputField(payload, "idempotencyKey", invalidMessage), "idempotencyKey", invalidMessage),
    confirm: identityInputBoolean(identityInputField(payload, "confirm", invalidMessage), "confirm", invalidMessage),
  };
}

function parseIdentityCompanyPair(
  sourceCompanyId: unknown,
  targetCompanyId: unknown,
): { sourceCompanyId: string; targetCompanyId: string } {
  const invalidMessage = "Os IDs do merge de empresas são inválidos.";
  const source = identityInputIdentifier(sourceCompanyId, "sourceCompanyId", invalidMessage);
  const target = identityInputIdentifier(targetCompanyId, "targetCompanyId", invalidMessage);
  if (source === target) throw new Error(`${invalidMessage}: relação.`);
  return { sourceCompanyId: source, targetCompanyId: target };
}

function parseIdentityMergeCompanyFieldChoices(value: unknown, invalidMessage: string): Record<string, string> {
  const payload = identityInputRecord(value, invalidMessage);
  const fieldNames: Record<string, string> = {
    legalName: "legal_name",
    tradeName: "trade_name",
    cnpj: "cnpj",
    site: "site",
    segment: "segment",
    city: "city",
    state: "state",
  };
  const parsed: Record<string, string> = {};
  for (const field of identityInputKeys(payload, invalidMessage)) {
    if (!Object.prototype.hasOwnProperty.call(fieldNames, field))
      throw new Error(`${invalidMessage}: fieldChoices.`);
    const choice = identityInputField(payload, field, invalidMessage);
    if (choice !== "source" && choice !== "target")
      throw new Error(`${invalidMessage}: fieldChoices.`);
    parsed[fieldNames[field]] = choice;
  }
  return parsed;
}

function parseIdentityMergeCompaniesInput(value: unknown): MergeCompaniesInput & { fieldChoices: Record<string, string> } {
  const invalidMessage = "O merge de empresas para persistência é inválido.";
  const payload = identityInputRecord(value, invalidMessage);
  assertIdentityAllowedInputKeys(payload, ["sourceCompanyId", "targetCompanyId", "fieldChoices", "reason", "idempotencyKey", "confirm"], invalidMessage);
  return {
    sourceCompanyId: identityInputIdentifier(identityInputField(payload, "sourceCompanyId", invalidMessage), "sourceCompanyId", invalidMessage),
    targetCompanyId: identityInputIdentifier(identityInputField(payload, "targetCompanyId", invalidMessage), "targetCompanyId", invalidMessage),
    fieldChoices: parseIdentityMergeCompanyFieldChoices(identityInputField(payload, "fieldChoices", invalidMessage), invalidMessage),
    reason: identityInputText(identityInputField(payload, "reason", invalidMessage), "reason", invalidMessage),
    idempotencyKey: identityInputIdempotencyKey(identityInputField(payload, "idempotencyKey", invalidMessage), "idempotencyKey", invalidMessage),
    confirm: identityInputBoolean(identityInputField(payload, "confirm", invalidMessage), "confirm", invalidMessage),
  };
}

function parseIdentityCanonicalChannel(value: unknown, invalidMessage: string): IdentityInput {
  const payload = identityInputRecord(value, invalidMessage);
  assertIdentityAllowedInputKeys(payload, ["type", "value"], invalidMessage);
  const type = identityInputField(payload, "type", invalidMessage);
  if (typeof type !== "string" || !partyChannelTypes.has(type as PartyChannelType))
    throw new Error(`${invalidMessage}: type.`);
  return {
    type: type as IdentityInput["type"],
    value: identityInputText(identityInputField(payload, "value", invalidMessage), "value", invalidMessage),
  };
}

function parseIdentityCanonicalChannels(value: unknown, key: string, invalidMessage: string): IdentityInput[] | undefined {
  if (value === undefined) return undefined;
  if (!isDenseRpcArray(value)) throw new Error(`${invalidMessage}: ${key}.`);
  return value.map((item) => parseIdentityCanonicalChannel(item, invalidMessage));
}

function parseIdentityCanonicalCompany(value: unknown, invalidMessage: string) {
  const payload = identityInputRecord(value, invalidMessage);
  assertIdentityAllowedInputKeys(payload, [
    "existingId", "existingCompanyId", "legalName", "tradeName", "cnpj", "site", "segment", "city", "state", "confirmCnpjReuse",
  ], invalidMessage);
  const existingId = identityOptionalInputIdentifier(payload, "existingId", invalidMessage);
  const existingCompanyId = identityOptionalInputIdentifier(payload, "existingCompanyId", invalidMessage);
  if (existingId && existingCompanyId && existingId !== existingCompanyId)
    throw new Error(`${invalidMessage}: existingId.`);
  return {
    existingId,
    existingCompanyId,
    legalName: identityOptionalInputTextValue(payload, "legalName", invalidMessage),
    tradeName: identityOptionalInputTextValue(payload, "tradeName", invalidMessage),
    cnpj: identityOptionalInputTextValue(payload, "cnpj", invalidMessage),
    site: identityOptionalInputTextValue(payload, "site", invalidMessage),
    segment: identityOptionalInputTextValue(payload, "segment", invalidMessage),
    city: identityOptionalInputTextValue(payload, "city", invalidMessage),
    state: identityOptionalInputTextValue(payload, "state", invalidMessage),
    confirmCnpjReuse: identityOptionalInputBoolean(payload, "confirmCnpjReuse", invalidMessage),
  };
}

function parseIdentityCanonicalContacts(value: unknown, invalidMessage: string) {
  if (value === undefined) return undefined;
  if (!isDenseRpcArray(value)) throw new Error(`${invalidMessage}: contacts.`);
  return value.map((item) => {
    const payload = identityInputRecord(item, invalidMessage);
    assertIdentityAllowedInputKeys(payload, ["existingId", "name", "role", "photoUrl", "primary", "channels"], invalidMessage);
    return {
      existingId: identityOptionalInputIdentifier(payload, "existingId", invalidMessage),
      name: identityOptionalInputTextValue(payload, "name", invalidMessage),
      role: identityOptionalInputTextValue(payload, "role", invalidMessage),
      photoUrl: identityOptionalInputTextValue(payload, "photoUrl", invalidMessage),
      primary: identityOptionalInputBoolean(payload, "primary", invalidMessage) ?? false,
      channels: parseIdentityCanonicalChannels(identityInputField(payload, "channels", invalidMessage), "channels", invalidMessage),
    };
  });
}

function parseIdentityCanonicalFieldValues(value: unknown, invalidMessage: string): Record<string, string | number | boolean | string[] | null> | undefined {
  if (value === undefined) return undefined;
  const payload = identityInputRecord(value, invalidMessage);
  const entries: Array<[string, string | number | boolean | string[] | null]> = [];
  for (const key of identityInputKeys(payload, invalidMessage)) {
    const fieldId = identityInputIdentifier(key, "fieldId", invalidMessage);
    if (fieldId !== key) throw new Error(`${invalidMessage}: fieldId.`);
    const fieldValue = identityInputField(payload, key, invalidMessage);
    if (fieldValue === null || typeof fieldValue === "string" || typeof fieldValue === "boolean") {
      entries.push([fieldId, fieldValue as string | boolean | null]);
      continue;
    }
    if (typeof fieldValue === "number" && Number.isFinite(fieldValue)) {
      entries.push([fieldId, fieldValue]);
      continue;
    }
    if (isDenseRpcArray(fieldValue) && fieldValue.every((item) => typeof item === "string")) {
      entries.push([fieldId, identityInputIdentifierArray(fieldValue, "fieldValue", invalidMessage)]);
      continue;
    }
    throw new Error(`${invalidMessage}: fieldValues.`);
  }
  return Object.fromEntries(entries);
}

function parseIdentityCanonicalLead(value: unknown, invalidMessage: string) {
  const payload = identityInputRecord(value, invalidMessage);
  assertIdentityAllowedInputKeys(payload, [
    "name", "company", "role", "potentialValue", "photoUrl", "email", "phone", "site", "instagram",
    "biggestPain", "biggestObjection", "bestOverdelivery", "city", "state", "segment", "source", "priority",
  ], invalidMessage);
  const potentialValue = identityInputField(payload, "potentialValue", invalidMessage);
  if (potentialValue !== undefined && potentialValue !== null
    && (typeof potentialValue !== "number" || !Number.isFinite(potentialValue) || potentialValue < 0))
    throw new Error(`${invalidMessage}: potentialValue.`);
  const priority = identityInputField(payload, "priority", invalidMessage);
  if (priority !== undefined && priority !== null && priority !== "low" && priority !== "medium" && priority !== "high")
    throw new Error(`${invalidMessage}: priority.`);
  const parsedPotentialValue = potentialValue === undefined || potentialValue === null ? undefined : potentialValue;
  const parsedPriority = priority === undefined || priority === null ? undefined : priority as "low" | "medium" | "high";
  return {
    name: identityOptionalInputTextValue(payload, "name", invalidMessage),
    company: identityOptionalInputTextValue(payload, "company", invalidMessage),
    role: identityOptionalInputTextValue(payload, "role", invalidMessage),
    potentialValue: parsedPotentialValue,
    photoUrl: identityOptionalInputTextValue(payload, "photoUrl", invalidMessage),
    email: identityOptionalInputTextValue(payload, "email", invalidMessage),
    phone: identityOptionalInputTextValue(payload, "phone", invalidMessage),
    site: identityOptionalInputTextValue(payload, "site", invalidMessage),
    instagram: identityOptionalInputTextValue(payload, "instagram", invalidMessage),
    biggestPain: identityOptionalInputTextValue(payload, "biggestPain", invalidMessage),
    biggestObjection: identityOptionalInputTextValue(payload, "biggestObjection", invalidMessage),
    bestOverdelivery: identityOptionalInputTextValue(payload, "bestOverdelivery", invalidMessage),
    city: identityOptionalInputTextValue(payload, "city", invalidMessage),
    state: identityOptionalInputTextValue(payload, "state", invalidMessage),
    segment: identityOptionalInputTextValue(payload, "segment", invalidMessage),
    source: identityOptionalInputTextValue(payload, "source", invalidMessage),
    priority: parsedPriority,
  };
}

function parseIdentityCreateOrReuseCompanyLeadInput(value: unknown): CreateOrReuseCompanyLeadInput {
  const invalidMessage = "A criação canônica para persistência é inválida.";
  const payload = identityInputRecord(value, invalidMessage);
  assertIdentityAllowedInputKeys(payload, [
    "idempotencyKey", "company", "contacts", "primaryContactId", "channels", "lead", "funnelId", "stageId",
    "tagIds", "triggerIds", "fieldValues", "initialNote", "confirmCnpjReuse",
  ], invalidMessage);
  const primaryContactId = identityInputField(payload, "primaryContactId", invalidMessage);
  const tagIds = identityInputField(payload, "tagIds", invalidMessage);
  const triggerIds = identityInputField(payload, "triggerIds", invalidMessage);
  return {
    idempotencyKey: identityInputIdempotencyKey(identityInputField(payload, "idempotencyKey", invalidMessage), "idempotencyKey", invalidMessage),
    company: parseIdentityCanonicalCompany(identityInputField(payload, "company", invalidMessage), invalidMessage),
    contacts: parseIdentityCanonicalContacts(identityInputField(payload, "contacts", invalidMessage), invalidMessage),
    primaryContactId: primaryContactId === null || primaryContactId === undefined
      ? primaryContactId
      : identityInputIdentifier(primaryContactId, "primaryContactId", invalidMessage),
    channels: parseIdentityCanonicalChannels(identityInputField(payload, "channels", invalidMessage), "channels", invalidMessage),
    lead: parseIdentityCanonicalLead(identityInputField(payload, "lead", invalidMessage), invalidMessage),
    funnelId: identityInputIdentifier(identityInputField(payload, "funnelId", invalidMessage), "funnelId", invalidMessage),
    stageId: identityInputIdentifier(identityInputField(payload, "stageId", invalidMessage), "stageId", invalidMessage),
    tagIds: tagIds === undefined ? undefined : identityInputIdentifierArray(tagIds, "tagIds", invalidMessage),
    triggerIds: triggerIds === undefined ? undefined : identityInputIdentifierArray(triggerIds, "triggerIds", invalidMessage),
    fieldValues: parseIdentityCanonicalFieldValues(identityInputField(payload, "fieldValues", invalidMessage), invalidMessage),
    initialNote: identityOptionalInputTextValue(payload, "initialNote", invalidMessage),
    confirmCnpjReuse: identityOptionalInputBoolean(payload, "confirmCnpjReuse", invalidMessage),
  };
}

async function identityAuthenticatedOwnerId(
  client: SupabaseClient,
  invalidMessage: string,
): Promise<string> {
  const { data, error } = await client.auth.getUser();
  if (error || !data.user) throw error ?? new Error(`${invalidMessage}: owner_id.`);
  return identityInputIdentifier(data.user.id, "owner_id", invalidMessage);
}

async function identityRequireOwnedCompany(
  client: SupabaseClient,
  companyId: string,
  ownerId: string,
  invalidMessage: string,
): Promise<void> {
  const result = await client
    .from("companies")
    .select("id,owner_id")
    .eq("id", companyId)
    .eq("owner_id", ownerId)
    .maybeSingle();
  if (result.error) throw result.error;
  if (!result.data || !isRpcRecord(result.data))
    throw new Error(`${invalidMessage}: companyId.`);
  const rawId = rpcField(result.data, "id");
  const rawOwnerId = rpcField(result.data, "owner_id");
  const rowId = identityInputIdentifier(rawId, "id", invalidMessage);
  const rowOwnerId = identityInputIdentifier(rawOwnerId, "owner_id", invalidMessage);
  if (rawId !== rowId || rawOwnerId !== rowOwnerId || rowId !== companyId || rowOwnerId !== ownerId)
    throw new Error(`${invalidMessage}: companyId.`);
}

const missingCapabilityCodes = new Set(["PGRST202", "42883"]);

const disabledCapabilities = {
  schemaVersion: 0,
  identityFoundation: false,
  canonicalLeadCreation: false,
  canonicalLeadCreationCanaryReady: false,
  canonicalLeadCreationCanary: false,
  taskCommands: false,
  emailInbox: false,
  emailSend: false,
  aiAssistance: false,
  aiAutoSend: false,
  flowDefinitions: false,
  flowPublication: false,
  flowExecution: false,
  finance: false,
} as const;

export async function loadIdentityCapabilities(client: SupabaseClient): Promise<IdentityCapabilities> {
  const { data, error } = await client.rpc("get_app_capabilities");
  if (error) {
    return missingCapabilityCodes.has(String(error.code))
      ? { status: "not_installed", ...disabledCapabilities }
      : { status: "degraded", ...disabledCapabilities, message: error.message };
  }
  if (!isRpcRecord(data)) {
    return { status: "degraded", ...disabledCapabilities, message: "Contrato de capabilities inválido." };
  }
  const payload = data;
  const schemaVersion = rpcField(payload, "schema_version");
  const identityFoundation = rpcField(payload, "identity_foundation");
  const canonicalLeadCreation = rpcField(payload, "canonical_lead_creation");
  const optionalCapabilityKeys = [
    "canonical_lead_creation_canary_ready",
    "canonical_lead_creation_canary",
    "task_commands",
    "email_inbox",
    "email_send",
    "ai_assistance",
    "ai_auto_send",
    "flow_definitions",
    "flow_publication",
    "flow_execution",
    "finance",
  ] as const;
  if (
    typeof schemaVersion !== "number"
    || !Number.isSafeInteger(schemaVersion)
    || schemaVersion < 1
    || typeof identityFoundation !== "boolean"
    || typeof canonicalLeadCreation !== "boolean"
    || optionalCapabilityKeys.some((key) => {
      const value = rpcField(payload, key);
      return value !== undefined && typeof value !== "boolean";
    })
  ) {
    return { status: "degraded", ...disabledCapabilities, message: "Contrato de capabilities inválido." };
  }
  const capabilityEnabled = (key: (typeof optionalCapabilityKeys)[number]) => rpcField(payload, key) === true;
  return {
    status: identityFoundation ? "available" : "not_installed",
    schemaVersion,
    identityFoundation,
    canonicalLeadCreation,
    canonicalLeadCreationCanaryReady: capabilityEnabled("canonical_lead_creation_canary_ready"),
    canonicalLeadCreationCanary: capabilityEnabled("canonical_lead_creation_canary"),
    taskCommands: capabilityEnabled("task_commands"),
    // Capability ausente em contrato mais antigo permanece desligada.
    emailInbox: capabilityEnabled("email_inbox"),
    emailSend: capabilityEnabled("email_send"),
    aiAssistance: capabilityEnabled("ai_assistance"),
    aiAutoSend: capabilityEnabled("ai_auto_send"),
    // Definição, publicação e execução de flows nascem desligadas e continuam
    // desligadas em contrato mais antigo, que não declara essas chaves.
    flowDefinitions: capabilityEnabled("flow_definitions"),
    flowPublication: capabilityEnabled("flow_publication"),
    flowExecution: capabilityEnabled("flow_execution"),
    // O domínio Financeiro nasce desligado e continua desligado em contrato
    // mais antigo, que sequer declara a chave.
    finance: capabilityEnabled("finance"),
  };
}

export type IdentityRows = [
  CompanyRow[], ContactRow[], PartyChannelRow[], LeadContactRow[], LeadRelationRow[], IdentityCandidateRow[],
];

const emptyIdentityRows = (): IdentityRows => [[], [], [], [], [], []];

const identityReadCompanyStatuses = new Set(["active", "archived", "merged"]);
const identityReadChannelTypes = new Set(["email", "phone", "instagram"]);
const identityReadChannelStatuses = new Set(["active", "inactive", "shared"]);
const identityReadRelationTypes = new Set(["cross_funnel", "merged"]);
const identityReadCandidateStatuses = new Set(["pending", "accepted", "rejected"]);

type IdentityReadRow = Record<string, unknown>;

function identityReadInvalidMessage(table: string): string {
  return `Uma linha remota de ${table} é inválida`;
}

function identityReadField(row: IdentityReadRow, key: string, table: string): unknown {
  return identityInputField(row, key, identityReadInvalidMessage(table));
}

function identityReadRecord(value: unknown, table: string): IdentityReadRow {
  if (!isRpcRecord(value)) throw new Error(`${identityReadInvalidMessage(table)}.`);
  return value;
}

function identityReadRows(value: unknown, table: string): IdentityReadRow[] {
  if (!isDenseRpcArray(value)) throw new Error(`${identityReadInvalidMessage(table)}: conjunto.`);
  return value.map((row) => identityReadRecord(row, table));
}

function identityReadStrictIdentifier(row: IdentityReadRow, key: string, table: string): string {
  const invalidMessage = identityReadInvalidMessage(table);
  const raw = identityReadField(row, key, table);
  const normalized = identityInputIdentifier(raw, key, invalidMessage);
  if (raw !== normalized) throw new Error(`${invalidMessage}: ${key}.`);
  return normalized;
}

function identityReadOptionalIdentifier(row: IdentityReadRow, key: string, table: string): string | undefined {
  const raw = identityReadField(row, key, table);
  if (raw === null || raw === undefined) return undefined;
  return identityReadStrictIdentifier(row, key, table);
}

function identityReadRequiredText(row: IdentityReadRow, key: string, table: string): string {
  return identityInputText(identityReadField(row, key, table), key, identityReadInvalidMessage(table));
}

function identityReadOptionalText(row: IdentityReadRow, key: string, table: string): string | undefined {
  const raw = identityReadField(row, key, table);
  if (raw === null || raw === undefined) return undefined;
  if (typeof raw !== "string") throw new Error(`${identityReadInvalidMessage(table)}: ${key}.`);
  return raw;
}

function identityReadOptionalBoolean(row: IdentityReadRow, key: string, table: string): boolean | undefined {
  const raw = identityReadField(row, key, table);
  if (raw === null || raw === undefined) return undefined;
  if (typeof raw !== "boolean") throw new Error(`${identityReadInvalidMessage(table)}: ${key}.`);
  return raw;
}

function identityReadOptionalEnum(
  row: IdentityReadRow,
  key: string,
  table: string,
  values: ReadonlySet<string>,
): string | undefined {
  const raw = identityReadField(row, key, table);
  if (raw === null || raw === undefined) return undefined;
  if (typeof raw !== "string" || !values.has(raw))
    throw new Error(`${identityReadInvalidMessage(table)}: ${key}.`);
  return raw;
}

function identityReadRequiredEnum(
  row: IdentityReadRow,
  key: string,
  table: string,
  values: ReadonlySet<string>,
): string {
  const raw = identityReadField(row, key, table);
  if (typeof raw !== "string" || !values.has(raw))
    throw new Error(`${identityReadInvalidMessage(table)}: ${key}.`);
  return raw;
}

function identityReadRequiredTimestamp(row: IdentityReadRow, key: string, table: string): void {
  identityReadRequiredText(row, key, table);
}

function identityReadOptionalTimestamp(row: IdentityReadRow, key: string, table: string): void {
  identityReadOptionalText(row, key, table);
}

function identityReadOwner(row: IdentityReadRow, ownerId: string, table: string): void {
  const rowOwnerId = identityReadStrictIdentifier(row, "owner_id", table);
  if (rowOwnerId !== ownerId) throw new Error(`${identityReadInvalidMessage(table)}: owner_id.`);
}

function identityReadUniqueIds(rows: readonly IdentityReadRow[], table: string): Map<string, IdentityReadRow> {
  const byId = new Map<string, IdentityReadRow>();
  for (const row of rows) {
    const id = identityReadStrictIdentifier(row, "id", table);
    if (byId.has(id)) throw new Error(`${identityReadInvalidMessage(table)}: id.`);
    byId.set(id, row);
  }
  return byId;
}

function identityReadConfidence(row: IdentityReadRow, table: string): void {
  const raw = identityReadField(row, "confidence", table);
  if (raw === null || raw === undefined) return;
  const value = typeof raw === "number" ? raw : typeof raw === "string" && raw.trim() ? Number(raw) : NaN;
  if (!Number.isFinite(value) || value < 0 || value > 1)
    throw new Error(`${identityReadInvalidMessage(table)}: confidence.`);
}

function validateIdentityRows(
  rows: IdentityRows,
  ownerId: string,
): void {
  const [companyRows, contactRows, channelRows, leadContactRows, leadRelationRows, candidateRows] = rows;
  const companyById = identityReadUniqueIds(companyRows, "empresa");
  const contactById = identityReadUniqueIds(contactRows, "contato");
  identityReadUniqueIds(channelRows, "canal");
  identityReadUniqueIds(leadRelationRows, "relação de lead");
  identityReadUniqueIds(candidateRows, "candidato de identidade");

  for (const row of companyRows) {
    identityReadOwner(row, ownerId, "empresa");
    const id = identityReadStrictIdentifier(row, "id", "empresa");
    const legalName = identityReadOptionalText(row, "legal_name", "empresa");
    const legacyName = identityReadOptionalText(row, "name", "empresa");
    if (!legalName?.trim() && !legacyName?.trim())
      throw new Error(`${identityReadInvalidMessage("empresa")}: legal_name.`);
    const status = identityReadOptionalEnum(row, "status", "empresa", identityReadCompanyStatuses);
    const mergedIntoId = identityReadOptionalIdentifier(row, "merged_into_id", "empresa");
    identityReadRequiredTimestamp(row, "created_at", "empresa");
    identityReadOptionalTimestamp(row, "updated_at", "empresa");
    if (status !== undefined && (status === "merged") !== Boolean(mergedIntoId))
      throw new Error(`${identityReadInvalidMessage("empresa")}: status.`);
    if (mergedIntoId !== undefined && (mergedIntoId === id || !companyById.has(mergedIntoId)))
      throw new Error(`${identityReadInvalidMessage("empresa")}: merged_into_id.`);
  }

  for (const row of contactRows) {
    identityReadOwner(row, ownerId, "contato");
    const id = identityReadStrictIdentifier(row, "id", "contato");
    const companyId = identityReadStrictIdentifier(row, "company_id", "contato");
    identityReadRequiredText(row, "name", "contato");
    identityReadOptionalText(row, "role", "contato");
    identityReadOptionalText(row, "photo_url", "contato");
    const mergedIntoId = identityReadOptionalIdentifier(row, "merged_into_id", "contato");
    identityReadRequiredTimestamp(row, "created_at", "contato");
    identityReadOptionalTimestamp(row, "updated_at", "contato");
    if (!companyById.has(companyId)) throw new Error(`${identityReadInvalidMessage("contato")}: company_id.`);
    if (mergedIntoId !== undefined) {
      const mergedInto = contactById.get(mergedIntoId);
      if (mergedIntoId === id || !mergedInto || identityReadStrictIdentifier(mergedInto, "company_id", "contato") !== companyId)
        throw new Error(`${identityReadInvalidMessage("contato")}: merged_into_id.`);
    }
  }

  for (const row of channelRows) {
    identityReadOwner(row, ownerId, "canal");
    identityReadStrictIdentifier(row, "id", "canal");
    const companyId = identityReadStrictIdentifier(row, "company_id", "canal");
    const contactId = identityReadOptionalIdentifier(row, "contact_id", "canal");
    identityReadRequiredEnum(row, "channel_type", "canal", identityReadChannelTypes);
    identityReadRequiredText(row, "value", "canal");
    identityReadRequiredText(row, "normalized_value", "canal");
    identityReadOptionalEnum(row, "status", "canal", identityReadChannelStatuses);
    identityReadOptionalBoolean(row, "is_primary", "canal");
    identityReadOptionalBoolean(row, "verified", "canal");
    identityReadRequiredTimestamp(row, "created_at", "canal");
    identityReadOptionalTimestamp(row, "updated_at", "canal");
    if (!companyById.has(companyId)) throw new Error(`${identityReadInvalidMessage("canal")}: company_id.`);
    if (contactId !== undefined) {
      const contact = contactById.get(contactId);
      if (!contact || identityReadStrictIdentifier(contact, "company_id", "contato") !== companyId)
        throw new Error(`${identityReadInvalidMessage("canal")}: contact_id.`);
    }
  }

  const leadContactKeys = new Set<string>();
  const primaryLeadIds = new Set<string>();
  for (const row of leadContactRows) {
    identityReadOwner(row, ownerId, "vínculo de contato");
    const leadId = identityReadStrictIdentifier(row, "lead_id", "vínculo de contato");
    const companyId = identityReadStrictIdentifier(row, "company_id", "vínculo de contato");
    const contactId = identityReadStrictIdentifier(row, "contact_id", "vínculo de contato");
    const key = `${leadId}\u0000${contactId}`;
    if (leadContactKeys.has(key)) throw new Error(`${identityReadInvalidMessage("vínculo de contato")}: lead_id/contact_id.`);
    leadContactKeys.add(key);
    const contact = contactById.get(contactId);
    if (!contact || identityReadStrictIdentifier(contact, "company_id", "contato") !== companyId)
      throw new Error(`${identityReadInvalidMessage("vínculo de contato")}: company_id.`);
    const primary = identityReadOptionalBoolean(row, "is_primary", "vínculo de contato") ?? false;
    if (primary && primaryLeadIds.has(leadId))
      throw new Error(`${identityReadInvalidMessage("vínculo de contato")}: is_primary.`);
    if (primary) primaryLeadIds.add(leadId);
    identityReadRequiredTimestamp(row, "created_at", "vínculo de contato");
  }

  const relationKeys = new Set<string>();
  for (const row of leadRelationRows) {
    identityReadOwner(row, ownerId, "relação de lead");
    const sourceLeadId = identityReadStrictIdentifier(row, "source_lead_id", "relação de lead");
    const targetLeadId = identityReadStrictIdentifier(row, "target_lead_id", "relação de lead");
    const relationType = identityReadRequiredEnum(row, "relation_type", "relação de lead", identityReadRelationTypes);
    const sourceFunnelId = identityReadOptionalIdentifier(row, "source_funnel_id", "relação de lead");
    const targetFunnelId = identityReadOptionalIdentifier(row, "target_funnel_id", "relação de lead");
    const key = `${sourceLeadId}\u0000${targetLeadId}\u0000${relationType ?? ""}`;
    if (relationKeys.has(key)) throw new Error(`${identityReadInvalidMessage("relação de lead")}: relação.`);
    relationKeys.add(key);
    if (sourceLeadId === targetLeadId) throw new Error(`${identityReadInvalidMessage("relação de lead")}: source_lead_id.`);
    if (relationType === "cross_funnel" && (!sourceFunnelId || !targetFunnelId || sourceFunnelId === targetFunnelId))
      throw new Error(`${identityReadInvalidMessage("relação de lead")}: funnel_id.`);
    identityReadRequiredTimestamp(row, "created_at", "relação de lead");
    identityReadOptionalTimestamp(row, "updated_at", "relação de lead");
  }

  for (const row of candidateRows) {
    identityReadOwner(row, ownerId, "candidato de identidade");
    identityReadStrictIdentifier(row, "id", "candidato de identidade");
    const companyId = identityReadOptionalIdentifier(row, "company_id", "candidato de identidade");
    const contactId = identityReadOptionalIdentifier(row, "contact_id", "candidato de identidade");
    const candidateCompanyId = identityReadOptionalIdentifier(row, "candidate_company_id", "candidato de identidade");
    const candidateContactId = identityReadOptionalIdentifier(row, "candidate_contact_id", "candidato de identidade");
    identityReadRequiredText(row, "reason", "candidato de identidade");
    identityReadConfidence(row, "candidato de identidade");
    identityReadRequiredEnum(row, "status", "candidato de identidade", identityReadCandidateStatuses);
    identityReadRequiredTimestamp(row, "created_at", "candidato de identidade");
    identityReadOptionalTimestamp(row, "reviewed_at", "candidato de identidade");
    if (companyId !== undefined && !companyById.has(companyId))
      throw new Error(`${identityReadInvalidMessage("candidato de identidade")}: company_id.`);
    if (contactId !== undefined) {
      const contact = contactById.get(contactId);
      if (!companyId || !contact || identityReadStrictIdentifier(contact, "company_id", "contato") !== companyId)
        throw new Error(`${identityReadInvalidMessage("candidato de identidade")}: contact_id.`);
    }
    if (candidateCompanyId !== undefined && !companyById.has(candidateCompanyId))
      throw new Error(`${identityReadInvalidMessage("candidato de identidade")}: candidate_company_id.`);
    if (candidateContactId !== undefined) {
      const candidateContact = contactById.get(candidateContactId);
      if (!candidateCompanyId || !candidateContact || identityReadStrictIdentifier(candidateContact, "company_id", "contato") !== candidateCompanyId)
        throw new Error(`${identityReadInvalidMessage("candidato de identidade")}: candidate_contact_id.`);
    }
  }
}

export async function loadIdentityRows(client: SupabaseClient, enabled: boolean, ownerIdValue: unknown) {
  if (!enabled) return { rows: emptyIdentityRows(), error: undefined };
  const ownerId = identityInputIdentifier(ownerIdValue, "owner_id", "A identidade remota é inválida");
  const results = await Promise.all([
    client.from("companies").select("*").eq("owner_id", ownerId).order("updated_at", { ascending: false }),
    client.from("contacts").select("*").eq("owner_id", ownerId).order("updated_at", { ascending: false }),
    client.from("party_channels").select("*").eq("owner_id", ownerId).order("updated_at", { ascending: false }),
    client.from("lead_contacts").select("*").eq("owner_id", ownerId),
    client.from("lead_relations").select("*").eq("owner_id", ownerId),
    client.from("identity_match_candidates").select("*").eq("owner_id", ownerId),
  ]);
  const failed = results.find((result) => result.error)?.error;
  if (failed) return { rows: emptyIdentityRows(), error: failed };
  const rows: IdentityRows = [
    identityReadRows(results[0].data, "empresa") as CompanyRow[],
    identityReadRows(results[1].data, "contato") as ContactRow[],
    identityReadRows(results[2].data, "canal") as PartyChannelRow[],
    identityReadRows(results[3].data, "vínculo de contato") as LeadContactRow[],
    identityReadRows(results[4].data, "relação de lead") as LeadRelationRow[],
    identityReadRows(results[5].data, "candidato de identidade") as IdentityCandidateRow[],
  ];
  validateIdentityRows(rows, ownerId);
  return { rows, error: undefined };
}

export const identityRemote = {
  async createOrReuseCompanyLead(client: SupabaseClient, input: CreateOrReuseCompanyLeadInput): Promise<CreateOrReuseCompanyLeadRemoteResult> {
    const parsedInput = parseIdentityCreateOrReuseCompanyLeadInput(input);
    const contactInputs = (parsedInput.contacts ?? []).map((contact) => ({
      existing_id: contact.existingId ?? null,
      name: contact.name ?? null,
      role: contact.role ?? null,
      photo_url: contact.photoUrl ?? null,
      primary: Boolean(contact.primary),
      channels: (contact.channels ?? []).map((channel) => ({ type: channel.type, value: channel.value })),
    }));
    const channels = [
      ...(parsedInput.channels ?? []).map((channel) => ({
        type: channel.type,
        value: channel.value,
        contact_id: null,
      })),
    ];
    const result = await client.rpc("create_or_reuse_company_lead", {
      p_idempotency_key: parsedInput.idempotencyKey,
      p_funnel_id: parsedInput.funnelId,
      p_stage_id: parsedInput.stageId,
      p_company_id: parsedInput.company.existingId ?? parsedInput.company.existingCompanyId ?? null,
      p_company: {
        legal_name: parsedInput.company.legalName ?? null,
        trade_name: parsedInput.company.tradeName ?? null,
        cnpj: parsedInput.company.cnpj ?? null,
        site: parsedInput.company.site ?? null,
        segment: parsedInput.company.segment ?? null,
        city: parsedInput.company.city ?? null,
        state: parsedInput.company.state ?? null,
      },
      p_contacts: contactInputs,
      p_primary_contact_id: parsedInput.primaryContactId ?? null,
      p_channels: channels,
      p_lead: {
        name: parsedInput.lead.name ?? null,
        role: parsedInput.lead.role ?? null,
        potential_value: parsedInput.lead.potentialValue ?? 0,
        photo_path: parsedInput.lead.photoUrl ?? null,
        email: parsedInput.lead.email ?? null,
        phone: parsedInput.lead.phone ?? null,
        site: parsedInput.lead.site ?? null,
        instagram: parsedInput.lead.instagram ?? null,
        biggest_pain: parsedInput.lead.biggestPain ?? null,
        biggest_objection: parsedInput.lead.biggestObjection ?? null,
        best_overdelivery: parsedInput.lead.bestOverdelivery ?? null,
        city: parsedInput.lead.city ?? null,
        state: parsedInput.lead.state ?? null,
        segment: parsedInput.lead.segment ?? null,
        source: parsedInput.lead.source ?? null,
        priority: parsedInput.lead.priority ?? "medium",
      },
      p_tag_ids: parsedInput.tagIds ?? [],
      p_trigger_ids: parsedInput.triggerIds ?? [],
      p_field_values: parsedInput.fieldValues ?? {},
      p_initial_note: parsedInput.initialNote ?? null,
      p_confirm_cnpj_reuse: Boolean(parsedInput.confirmCnpjReuse || parsedInput.company.confirmCnpjReuse),
    });
    if (result.error) throw result.error;
    if (!result.data || typeof result.data !== "object" || Array.isArray(result.data))
      throw new Error("Resposta inválida da criação canônica candidata.");
    const parsedResult = parseCreateOrReuseCompanyLeadResult(result.data as IdentityRpcResult);
    const requestedCompanyId = parsedInput.company.existingId ?? parsedInput.company.existingCompanyId;
    if (requestedCompanyId !== undefined && parsedResult.companyId !== requestedCompanyId)
      throw new Error("Resposta inválida da criação canônica candidata: empresa divergente.");
    return parsedResult;
  },
  async previewRelatedLeadInFunnel(client: SupabaseClient, sourceLeadId: unknown, targetFunnelId: unknown): Promise<RelatedLeadPreviewRemoteResult> {
    const parsedInput = parseIdentityRelatedLeadReferences(sourceLeadId, targetFunnelId);
    const result = await invokeIdentityRpc(client, "preview_related_lead_in_funnel", {
      p_source_lead_id: parsedInput.sourceLeadId,
      p_target_funnel_id: parsedInput.targetFunnelId,
    }, "Resposta inválida da prévia de continuidade.");
    const parsed = parseRelatedLeadPreview(result, parsedInput);
    if (parsed.sourceLeadId !== parsedInput.sourceLeadId || parsed.targetFunnelId !== parsedInput.targetFunnelId)
      throw new Error("Resposta inválida da prévia de continuidade: IDs divergentes.");
    return parsed;
  },
  async createRelatedLeadInFunnel(client: SupabaseClient, input: CreateRelatedLeadInput): Promise<CreateRelatedLeadRemoteResult> {
    const parsedInput = parseIdentityCreateRelatedLeadInput(input);
    const result = await invokeIdentityRpc(client, "create_related_lead_in_funnel", {
      p_source_lead_id: parsedInput.sourceLeadId,
      p_target_funnel_id: parsedInput.targetFunnelId,
      p_target_stage_id: parsedInput.targetStageId,
      p_contact_ids: parsedInput.contactIds ?? [],
      p_idempotency_key: parsedInput.idempotencyKey,
      p_reason: parsedInput.reason,
      p_confirm: parsedInput.confirm,
    }, "Resposta inválida da continuidade.");
    const parsed = parseCreateRelatedLeadResult(result);
    if (parsed.sourceLeadId !== parsedInput.sourceLeadId)
      throw new Error("Resposta inválida da continuidade: source_lead_id divergente.");
    return parsed;
  },
  async reviewIdentityCandidate(client: SupabaseClient, input: ReviewIdentityCandidateInput): Promise<ReviewIdentityCandidateRemoteResult> {
    const parsedInput = parseIdentityReviewCandidateInput(input);
    const result = await invokeIdentityRpc(client, "review_identity_candidate", {
      p_candidate_id: parsedInput.candidateId,
      p_decision: parsedInput.decision,
      p_reason: parsedInput.reason,
      p_idempotency_key: parsedInput.idempotencyKey,
      p_confirm: parsedInput.confirm,
    }, "Resposta inválida da revisão do candidato.");
    const parsed = parseReviewIdentityCandidateResult(result);
    if (parsed.candidateId !== parsedInput.candidateId || parsed.decision !== parsedInput.decision)
      throw new Error("Resposta inválida da revisão do candidato: identidade divergente.");
    return parsed;
  },
  async previewReclassifyPartyChannel(client: SupabaseClient, channelId: unknown, targetContactId?: unknown): Promise<ReclassifyPartyChannelPreviewRemoteResult> {
    const parsedChannelId = identityInputIdentifier(channelId, "channelId", "Os IDs da prévia do canal são inválidos.");
    const parsedTargetContactId = targetContactId === null || targetContactId === undefined
      ? undefined
      : identityInputIdentifier(targetContactId, "targetContactId", "Os IDs da prévia do canal são inválidos.");
    const result = await invokeIdentityRpc(client, "preview_reclassify_party_channel", {
      p_channel_id: parsedChannelId,
      p_target_contact_id: parsedTargetContactId ?? null,
    }, "Resposta inválida da prévia do canal.");
    const parsed = parseReclassifyPartyChannelPreviewResult(result);
    if (parsed.channelId !== parsedChannelId || parsed.toContactId !== parsedTargetContactId)
      throw new Error("Resposta inválida da prévia do canal: identidade divergente.");
    return parsed;
  },
  async reclassifyPartyChannel(client: SupabaseClient, input: ReclassifyPartyChannelInput): Promise<ReclassifyPartyChannelRemoteResult> {
    const parsedInput = parseIdentityReclassifyChannelInput(input);
    const result = await invokeIdentityRpc(client, "reclassify_party_channel", {
      p_channel_id: parsedInput.channelId,
      p_target_contact_id: parsedInput.targetContactId ?? null,
      p_reason: parsedInput.reason,
      p_idempotency_key: parsedInput.idempotencyKey,
      p_confirm: parsedInput.confirm,
    }, "Resposta inválida da reclassificação do canal.");
    const parsed = parseReclassifyPartyChannelResult(result);
    if (parsed.channelId !== parsedInput.channelId || parsed.targetContactId !== parsedInput.targetContactId)
      throw new Error("Resposta inválida da reclassificação do canal: identidade divergente.");
    return parsed;
  },
  async previewMergeCompanies(client: SupabaseClient, sourceCompanyId: unknown, targetCompanyId: unknown): Promise<MergeCompaniesPreviewRemoteResult> {
    const parsedInput = parseIdentityCompanyPair(sourceCompanyId, targetCompanyId);
    const result = await invokeIdentityRpc(client, "preview_merge_companies", {
      p_source_company_id: parsedInput.sourceCompanyId,
      p_target_company_id: parsedInput.targetCompanyId,
    }, "Resposta inválida da prévia do merge.");
    const parsed = parseMergeCompaniesPreviewResult(result);
    if (parsed.sourceCompanyId !== parsedInput.sourceCompanyId || parsed.targetCompanyId !== parsedInput.targetCompanyId)
      throw new Error("Resposta inválida da prévia do merge: empresas divergentes.");
    return parsed;
  },
  async mergeCompanies(client: SupabaseClient, input: MergeCompaniesInput): Promise<MergeCompaniesRemoteResult> {
    const parsedInput = parseIdentityMergeCompaniesInput(input);
    const parsedPair = parseIdentityCompanyPair(parsedInput.sourceCompanyId, parsedInput.targetCompanyId);
    const result = await invokeIdentityRpc(client, "merge_companies", {
      p_source_company_id: parsedPair.sourceCompanyId,
      p_target_company_id: parsedPair.targetCompanyId,
      p_field_choices: parsedInput.fieldChoices,
      p_reason: parsedInput.reason,
      p_idempotency_key: parsedInput.idempotencyKey,
      p_confirm: parsedInput.confirm,
    }, "Resposta inválida do merge.");
    const parsed = parseMergeCompaniesResult(result);
    if (parsed.sourceCompanyId !== parsedPair.sourceCompanyId || parsed.targetCompanyId !== parsedPair.targetCompanyId)
      throw new Error("Resposta inválida do merge: empresas divergentes.");
    return parsed;
  },
  async addCompany(client: SupabaseClient, ownerId: unknown, company: unknown) {
    const invalidMessage = "A empresa para persistência é inválida.";
    const parsed = parseIdentityCompanyCreateInput(ownerId, company);
    const authenticatedOwnerId = await identityAuthenticatedOwnerId(client, invalidMessage);
    const parsedOwnerId = identityInputIdentifier(parsed.row.owner_id, "ownerId", invalidMessage);
    if (parsedOwnerId !== authenticatedOwnerId) throw new Error(`${invalidMessage}: ownerId.`);
    const result = await client.from("companies").insert(parsed.row);
    if (result.error) throw result.error;
  },
  async updateCompany(client: SupabaseClient, id: unknown, patch: unknown) {
    const invalidMessage = "A atualização da empresa é inválida.";
    const parsed = parseIdentityCompanyUpdateInput(id, patch);
    const authenticatedOwnerId = await identityAuthenticatedOwnerId(client, invalidMessage);
    const result = await client
      .from("companies")
      .update(parsed.row)
      .eq("id", parsed.id)
      .eq("owner_id", authenticatedOwnerId)
      .select("id")
      .maybeSingle();
    if (result.error) throw result.error;
    if (!result.data) throw new Error("Empresa não encontrada ou sem permissão para atualização");
  },
  async addContact(client: SupabaseClient, ownerId: unknown, contact: unknown) {
    const invalidMessage = "O contato para persistência é inválido.";
    const parsed = parseIdentityContactCreateInput(ownerId, contact);
    const authenticatedOwnerId = await identityAuthenticatedOwnerId(client, invalidMessage);
    const parsedOwnerId = identityInputIdentifier(parsed.row.owner_id, "ownerId", invalidMessage);
    if (parsedOwnerId !== authenticatedOwnerId) throw new Error(`${invalidMessage}: ownerId.`);
    const companyId = identityInputIdentifier(parsed.row.company_id, "companyId", invalidMessage);
    await identityRequireOwnedCompany(client, companyId, authenticatedOwnerId, invalidMessage);
    const result = await client.from("contacts").insert(parsed.row);
    if (result.error) throw result.error;
  },
  async updateContact(client: SupabaseClient, id: unknown, patch: unknown) {
    const invalidMessage = "A atualização do contato é inválida.";
    const parsed = parseIdentityContactUpdateInput(id, patch);
    const authenticatedOwnerId = await identityAuthenticatedOwnerId(client, invalidMessage);
    const result = await client
      .from("contacts")
      .update(parsed.row)
      .eq("id", parsed.id)
      .eq("owner_id", authenticatedOwnerId)
      .select("id")
      .maybeSingle();
    if (result.error) throw result.error;
    if (!result.data) throw new Error("Contato não encontrado ou sem permissão para atualização");
  },
  async setLeadContacts(client: SupabaseClient, leadId: unknown, contactIds: unknown, primaryContactId?: unknown) {
    const parsed = parseIdentityLeadContactsInput(leadId, contactIds, primaryContactId);
    const result = await client.rpc("set_lead_contacts", {
      p_lead_id: parsed.leadId,
      p_contact_ids: parsed.contactIds,
      p_primary_contact_id: parsed.primaryContactId,
    });
    if (result.error) throw result.error;
  },
};
